// src/components/Payment/PaymentStatus.js
import React, { useEffect, useState, useCallback } from 'react';
import { CheckCircle, XCircle, Loader2, Clock, RefreshCw } from 'lucide-react';
import paymentService from '../../services/paymentService';
import { WaveLogo, OrangeMoneyLogo } from './PaymentMethodLogos';
import './PaymentStatus.css';

const TIMEOUT_MS = paymentService.TIMEOUT_MS;

export default function PaymentStatus({
  externalTransactionId,
  deepLinkUrl,
  paymentMode,
  amount,
  onSuccess,
  onFailed,
  onRetry,
}) {
  const [status, setStatus] = useState('PENDING');
  const [elapsed, setElapsed] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [manualLoading, setManualLoading] = useState(false);

  const handleManualCheck = useCallback(async () => {
    if (!externalTransactionId) return;
    setManualLoading(true);
    const result = await paymentService.checkPaymentStatus(externalTransactionId);
    setManualLoading(false);
    if (!result.success) {
      setErrorMsg(result.message || 'Vérification impossible');
      return;
    }
    setStatus(result.status);
    if (result.status === 'SUCCESS') onSuccess?.(result);
    else if (['FAILLED', 'CANCELED', 'REFUNDED'].includes(result.status)) {
      setErrorMsg('Le paiement a échoué ou a été annulé.');
      onFailed?.(result);
    }
  }, [externalTransactionId, onSuccess, onFailed]);

  useEffect(() => {
    if (!externalTransactionId) return undefined;

    const start = Date.now();
    const clock = setInterval(() => setElapsed(Date.now() - start), 1000);

    paymentService.startPolling(externalTransactionId, {
      onUpdate: (r) => setStatus(r.status),
      onSuccess: (r) => {
        setStatus('SUCCESS');
        onSuccess?.(r);
      },
      onFailed: () => {
        setStatus('FAILLED');
        setErrorMsg('Le paiement a échoué. Vous pouvez réessayer.');
        onFailed?.();
      },
      onTimeout: () => {
        setStatus('TIMEOUT');
        setErrorMsg('Délai de 15 minutes dépassé. Le paiement n\'a pas été confirmé.');
        onFailed?.();
      },
    });

    return () => {
      clearInterval(clock);
      paymentService.stopPolling();
    };
  }, [externalTransactionId, onSuccess, onFailed]);

  const progress = Math.min(100, (elapsed / TIMEOUT_MS) * 100);
  const remainingMin = Math.max(0, Math.ceil((TIMEOUT_MS - elapsed) / 60000));
  const isWave = paymentMode === 'WAVE';
  const MethodLogo = isWave ? WaveLogo : OrangeMoneyLogo;

  if (status === 'SUCCESS') {
    return (
      <div className="pmt-status pmt-status--success">
        <CheckCircle size={56} strokeWidth={1.5} />
        <h2>Paiement confirmé !</h2>
        <p>Votre acompte a bien été reçu.</p>
      </div>
    );
  }

  if (status === 'FAILLED' || status === 'TIMEOUT' || status === 'CANCELED') {
    return (
      <div className="pmt-status pmt-status--failed">
        <XCircle size={56} strokeWidth={1.5} />
        <h2>Paiement non confirmé</h2>
        <p>{errorMsg}</p>
        <div className="pmt-status__actions">
          <button type="button" className="pmt-status__btn pmt-status__btn--primary" onClick={onRetry}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pmt-status pmt-status--pending">
      <div className="pmt-status__brand">
        <MethodLogo size={52} />
      </div>
      <div className="pmt-status__spinner-wrap">
        <Loader2 size={48} className="pmt-status__spinner" />
      </div>
      <h2>En attente de validation</h2>
      <p>
        Validez le paiement de <strong>{amount ? `${Number(amount).toLocaleString('fr-FR')} FCFA` : 'l\'acompte'}</strong>
        {' '}sur votre téléphone {paymentMode === 'ORANGE' ? 'Orange Money' : 'Wave'}.
      </p>

      {isWave && deepLinkUrl && (
        <a
          href={deepLinkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="pmt-status__wave-btn"
        >
          Ouvrir Wave
        </a>
      )}

      <div className="pmt-status__timeout">
        <div className="pmt-status__timeout-bar">
          <div className="pmt-status__timeout-fill" style={{ width: `${progress}%` }} />
        </div>
        <span className="pmt-status__timeout-label">
          <Clock size={14} />
          Expire dans ~{remainingMin} min
        </span>
      </div>

      <button
        type="button"
        className="pmt-status__manual"
        onClick={handleManualCheck}
        disabled={manualLoading}
      >
        <RefreshCw size={14} className={manualLoading ? 'pmt-status__spin' : ''} />
        Vérifier manuellement
      </button>
    </div>
  );
}
